import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import PasswordInput from "../../common/PasswordInput";
import "../Auth.css";

const ResetPasswordConfirmPage = () => {
  const { uid, token } = useParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    try {
      await axios.post(`${SERVER_URL}/api/auth/users/reset_password_confirm/`, {
        uid,
        token,
        new_password: newPassword,
      });
      setMessage("Password reset successfully! Redirecting to sign-in...");
      setTimeout(() => navigate("/signin"), 2000);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Reset Password – Mend Grove</title>
        <meta
          name="description"
          content="Reset your Mend Grove account password securely. Enter your new password to regain access to your account."
        />
        <meta name="robots" content="noindex, follow" />
        <link
          rel="canonical"
          href="https://mendgrove.com/reset-password-confirm"
        />
        <meta property="og:title" content="Reset Password – Mend Grove" />
        <meta
          property="og:description"
          content="Reset your Mend Grove account password securely. Enter your new password to regain access to your account."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://mendgrove.com/reset-password-confirm"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Reset Password – Mend Grove" />
        <meta
          name="twitter:description"
          content="Reset your Mend Grove account password securely. Enter your new password to regain access to your account."
        />
        <meta name="twitter:image" content="https://mendgrove.com/logo192.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Mend Grove",
              "url": "https://mendgrove.com/",
              "logo": "https://mendgrove.com/logo192.png",
              "sameAs": [
                "https://www.linkedin.com/company/mendgrove",
                "https://www.facebook.com/mendgrove"
              ],
              "description": "Mend Grove offers gentle, science-based brain-training games to support cognitive recovery and everyday mental exercise.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@mendgrove.com",
                "contactType": "customer support",
                "url": "https://mendgrove.com/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="auth-card">
        <h2 className="auth-title">Reset Password</h2>
        <form onSubmit={handleResetPassword}>
          <div className="auth-field">
            <label
              htmlFor="newPassword"
              className="auth-label"
            >
              New Password
            </label>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoFocus
              autoComplete="new-password"
            />
          </div>
          <div className="auth-field">
            <label
              htmlFor="confirmPassword"
              className="auth-label"
            >
              Confirm Password
            </label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>
          {message && (
            <p
              className="auth-message auth-message--success"
              aria-live="polite"
            >
              {message}
            </p>
          )}
          {error && (
            <p className="auth-message auth-message--error" aria-live="polite">
              {error}
            </p>
          )}
          <button type="submit" className="auth-submit" disabled={isLoading}>
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordConfirmPage;
